import { type InferResponseType, unwrap } from "@nexxt/api-client";
import type { ListErpFilesInput } from "@nexxt/contracts/integration";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the files to the ERP (spec v0.3 §7.5, ADR-258): the queue of the files to import, by cursor, and a
// file with the rows Business Central refused and the log of its import.

const erpFiles = api.integration["erp-files"];

export type ErpFile = InferResponseType<typeof erpFiles.$get, 200>["items"][number];
export type ErpFileDetail = InferResponseType<(typeof erpFiles)[":erpFileId"]["$get"], 200>;

/** The filters of the list input, without the paging the queries add. */
export type ErpFileFilters = Pick<ListErpFilesInput, "status" | "kind">;

const keys = { all: ["erp-files"] as const };

export const erpFileQueries = {
  ...keys,

  list: ({ status, kind }: ErpFileFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.all, "list", { status, kind }],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          erpFiles.$get(
            {
              query: {
                ...(status && { status }),
                ...(kind && { kind }),
                ...(pageParam && { cursor: pageParam }),
                limit: "50",
              },
            },
            { init: { signal } },
          ),
        ),
      initialPageParam: "",
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    }),

  detail: (erpFileId: string) =>
    queryOptions({
      queryKey: [...keys.all, "detail", erpFileId],
      queryFn: ({ signal }) => unwrap(erpFiles[":erpFileId"].$get({ param: { erpFileId } }, { init: { signal } })),
    }),
};

/** Where the browser downloads a file, as the shared folder has it. */
export const erpFileContentHref = (erpFileId: string): string =>
  erpFiles[":erpFileId"].content.$url({ param: { erpFileId } }).href;
